import React, { CSSProperties } from 'react';
import { RowComponentProps } from 'react-window';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
    AccessColumn,
    ACCESS_COLUMNS,
} from '@components/admin/users/study-membership/trialos-study-membership/columns';
import {
    AccessMap,
    currentAccess,
    MIXED,
    StudyOption,
} from '@components/admin/users/study-membership/trialos-study-membership/helpers';
import { useTranslation } from 'hooks';
import { RiskIQAccess } from 'types/graphql-global-types';

interface AccessOption {
    value: RiskIQAccess;
    label: string;
}

interface AccessCellProps {
    column: AccessColumn;
    value: RiskIQAccess | typeof MIXED | null;
    disabled: boolean;
    testId: string;
    onChange: (level: RiskIQAccess) => void;
}

export const AccessCell = ({ column, value, disabled, testId, onChange }: AccessCellProps) => {
    const { t } = useTranslation();
    const options: AccessOption[] = column.levels.map((level) => ({ value: level, label: t(column.labelKey(level)) }));
    const selected = options.find((option) => option.value === value) ?? null;

    // "Mixed" is shown in place of a value but is never an option
    const renderValue = (element: React.ReactElement<HTMLSpanElement>) =>
        value === MIXED ? React.cloneElement(element, {}, t('uam:trialOsStudyMembershipDialog.mixed')) : element;

    return (
        <div className="access-cell" data-testid={testId}>
            <DropDownList
                data={options}
                textField="label"
                dataItemKey="value"
                value={selected}
                valueRender={renderValue}
                disabled={disabled}
                style={{ width: '100%' }}
                onChange={(e: DropDownListChangeEvent) => onChange((e.value as AccessOption).value)}
            />
        </div>
    );
};

export interface StudyAccessRowProps {
    studies: StudyOption[];
    original: AccessMap;
    edits: AccessMap;
    disabled: boolean;
    onChangeStudy: (studyId: string, level: RiskIQAccess) => void;
}

const StudyAccessRow = ({
    index,
    style,
    studies,
    original,
    edits,
    disabled,
    onChangeStudy,
}: RowComponentProps<StudyAccessRowProps>) => {
    const study = studies[index];

    return (
        <div className="access-grid access-row" style={style as CSSProperties} data-testid={`study-row-${study.id}`}>
            <div className="access-study">{study.studyNumber}</div>
            {ACCESS_COLUMNS.map((column) => (
                <AccessCell
                    key={column.key}
                    column={column}
                    value={currentAccess(study.id, original, edits)}
                    disabled={disabled}
                    testId={`access-${study.id}-${column.key}`}
                    onChange={(level) => onChangeStudy(study.id, level)}
                />
            ))}
        </div>
    );
};

export default StudyAccessRow;
